import { useThemeStore } from './theme';

/**
 * Chart.js paints to a canvas, so none of the CSS token swapping in
 * `app/styles/dark.css` reaches it — every chart color is a literal in the
 * chart config. This is where those literals live so they can follow the theme.
 *
 * The LIGHT values are byte-identical to what the chart configs hardcoded
 * before (including Chart.js's own `#666` default for axis text), so light mode
 * is untouched.
 */
export type ChartTheme = {
  /** Full-height track drawn behind each bar in DivergingBarChart. */
  barTrack: string;
  /** Value labels, drawn on the track just past the end of each bar. */
  dataLabel: string;
  /** The min/zero/max grid lines. */
  gridLine: string;
  /** Axis ticks and axis titles. */
  axisText: string;
  /** Band behind an incomplete week/month, and its label. */
  incompleteFill: string;
  incompleteText: string;
};

const LIGHT: ChartTheme = {
  barTrack: 'rgba(234, 234, 234, 0.4)',
  dataLabel: '#000',
  gridLine: '#E0E0E0',
  axisText: '#666',
  incompleteFill: 'rgba(23, 80, 172, 0.08)',
  incompleteText: '#1750ac',
};

/*
 * The track is an alpha wash rather than a flat token because it has to sit on
 * the card without competing with the red/green bars on top of it — 8% white on
 * neutral-1 lands at ~#313131, the same "barely there" weight 40% #eaeaea has on
 * a white card. The labels then sit on that, so they take neutral-12.
 */
const DARK: ChartTheme = {
  barTrack: 'rgba(255, 255, 255, 0.08)',
  dataLabel: '#f5f5f5',
  gridLine: 'rgba(255, 255, 255, 0.2)',
  axisText: '#bfbfbf',
  incompleteFill: 'rgba(109, 146, 204, 0.2)',
  incompleteText: '#9db9e3',
};

/**
 * Subscribing to the store (rather than reading the CSS variables at draw time)
 * is what makes a chart repaint on toggle: a theme change re-renders the
 * component, which hands Chart.js a new options object.
 */
export const useChartTheme = (): ChartTheme =>
  useThemeStore((state) => (state.theme === 'dark' ? DARK : LIGHT));

/**
 * Resolves a design token to the hex Chart.js needs. Canvas has no cascade, so
 * `fillStyle: 'var(--color-dust-red-6)'` silently paints nothing — the value has
 * to be read off the document first.
 *
 * Safe to call from module scope (inside a config object or a color callback),
 * because it reads at call time: `AntdProvider` remounts the tree when the theme
 * flips, so every one of these re-runs against the new `.dark` values.
 */
let cache: { key: string; values: Map<string, string> } | null = null;

const isDark = () => document.documentElement.classList.contains('dark');

/**
 * For the handful of chart literals whose nearest token isn't a pixel-exact
 * match. Light mode keeps the exact color that was hardcoded there; only dark
 * mode picks up the token. Prefer plain `chartColor` where the token's light
 * value already equals the literal — most of them do.
 */
export const chartColorPinned = (
  token: `--color-${string}`,
  lightValue: string
): string => (isDark() ? chartColor(token) : lightValue);

export const chartColor = (token: `--color-${string}`): string => {
  // Keyed on the class list, which is what carries `.dark` — so the cache
  // invalidates itself on toggle without needing to know about the store.
  // Without this, a per-datapoint color callback would run getComputedStyle
  // (a layout read) once per point on every draw.
  const key = document.documentElement.className;
  if (!cache || cache.key !== key) cache = { key, values: new Map() };

  const cached = cache.values.get(token);
  if (cached !== undefined) return cached;

  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(token)
    .trim();
  cache.values.set(token, value);
  return value;
};
